/**
 * Bundle Builder
 *
 * Reads slot config from <script data-bundle-config>, tracks per-slot
 * selections, validates completeness, and submits to /cart/add.js as
 * multiple line items sharing a `_bundle_id` line item property so Juo
 * (or a cart transform) can recognize them as one bundle.
 *
 * Slots are rendered as <select> dropdowns. Selecting an option updates
 * the live product preview card beneath the dropdown.
 */

(function () {
  'use strict';

  class BundleBuilder {
    constructor(root) {
      this.root = root;
      this.productId = root.dataset.productId;
      this.productHandle = root.dataset.productHandle;
      this.productTitle = root.dataset.productTitle || document.title;

      const configEl = root.querySelector('[data-bundle-config]');
      if (!configEl) {
        console.warn('[BundleBuilder] No config payload found');
        return;
      }

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('[BundleBuilder] Failed to parse config', e);
        return;
      }

      this.selections = new Map(); // slot_uid -> Set of variant IDs
      this.atc = root.querySelector('[data-bundle-add]');
      this.atcLabelEl = this.atc ? this.atc.querySelector('[data-bundle-cta-label]') : null;
      this.countEl = root.querySelector('[data-bundle-selected-count]');
      this.errorEl = root.querySelector('[data-bundle-error]');
      this.preorderShippingEl = root.querySelector('[data-bundle-preorder-shipping]');
      this.notifyEl = root.querySelector('[data-bundle-notify]');
      this.currentNotify = null;

      // variant IDs whose product is on pre-order (out of stock, still sellable),
      // with the Stoq expected-shipping info when available; sold-out variants
      // (not sellable at all) block the ATC. Both get the Stoq notify-me button.
      this.preorderVariants = new Map();
      this.soldoutVariants = new Map();
      this.notifyVariants = new Map();
      this.config.forEach((slot) => {
        (slot.products || []).forEach((p) => {
          if (p.preorder) {
            this.preorderVariants.set(p.variant_id, {
              date: p.preorder_date || '',
              shipping: p.preorder_shipping || '',
            });
          }
          if (p.available === false) {
            this.soldoutVariants.set(p.variant_id, p);
          }
          if (p.available === false || p.preorder) {
            this.notifyVariants.set(p.variant_id, p);
          }
        });
      });

      this.init();
    }

    init() {
      // Pre-fill fixed slots from their variant IDs array
      this.config.forEach((slot) => {
        if (slot.selection_mode === 'fixed') {
          const ids = (slot.fixed_variant_ids || []).filter(Boolean);
          this.selections.set(slot.slot_uid, new Set(ids));
        } else {
          this.selections.set(slot.slot_uid, new Set());
        }
      });

      // Also read any fixed items directly from the DOM (fallback / future-proofing)
      this.root.querySelectorAll('[data-bundle-slot][data-selection-mode="fixed"]').forEach((slotEl) => {
        const uid = slotEl.dataset.slotUid;
        if (!this.selections.has(uid)) this.selections.set(uid, new Set());
        const set = this.selections.get(uid);
        slotEl.querySelectorAll('[data-bundle-fixed-item]').forEach((item) => {
          const vid = parseInt(item.dataset.variantId, 10);
          if (vid) set.add(vid);
        });
      });

      this.bindEvents();

      // Sync JS state with the pre-selected (first available) option in picker slots
      this.root.querySelectorAll('[data-bundle-slot]').forEach((slotEl) => {
        const uid = slotEl.dataset.slotUid;
        const mode = slotEl.dataset.selectionMode;
        if (mode === 'fixed') return;

        const selectEl = slotEl.querySelector('[data-bundle-select]');
        if (selectEl && selectEl.value) {
          this.handleSelectChange(uid, selectEl, slotEl);
        }
      });

      this.updateUI();
    }

    bindEvents() {
      this.root.querySelectorAll('[data-bundle-slot]').forEach((slotEl) => {
        const uid = slotEl.dataset.slotUid;
        const mode = slotEl.dataset.selectionMode;

        if (mode === 'fixed') return;

        const selectEl = slotEl.querySelector('[data-bundle-select]');
        if (selectEl) {
          selectEl.addEventListener('change', () => {
            this.handleSelectChange(uid, selectEl, slotEl);
          });
        }
      });

      if (this.atc) {
        this.atc.addEventListener('click', () => this.submit());
      }

      if (this.notifyEl) {
        this.notifyEl.addEventListener('click', () => {
          const target = this.currentNotify;
          if (!target) return;
          if (!window._RestockRocket || typeof window._RestockRocket.openModal !== 'function') {
            console.warn('[BundleBuilder] Stoq is not loaded; cannot open notify-me modal');
            return;
          }
          window._RestockRocket.openModal(
            {
              id: target.product.id,
              title: target.product.title,
              handle: target.product.handle,
              variants: [{ id: target.variantId, available: false }],
            },
            target.variantId
          );
        });
      }
    }

    handleSelectChange(uid, selectEl, slotEl) {
      const current = this.selections.get(uid);
      const selectedOption = selectEl.options[selectEl.selectedIndex];
      const variantId = selectEl.value ? parseInt(selectEl.value, 10) : null;

      current.clear();
      if (variantId) current.add(variantId);

      // Update left-side thumbnail and name when a product is chosen
      if (variantId && selectedOption) {
        const thumb = slotEl.querySelector('[data-bundle-thumb]');
        const nameEl = slotEl.querySelector('[data-bundle-slot-name]');
        const imageSrc = selectedOption.dataset.image || '';

        if (thumb) {
          thumb.src = imageSrc;
          thumb.alt = selectedOption.dataset.title || '';
        }
        if (nameEl && selectedOption.dataset.title) {
          nameEl.textContent = selectedOption.dataset.title;
        }
      }

      this.updateUI();
    }

    isComplete() {
      return this.config.every((slot) => {
        const sel = this.selections.get(slot.slot_uid);
        return sel && sel.size > 0;
      });
    }

    selectedCount() {
      let n = 0;
      this.selections.forEach((set) => (n += set.size));
      return n;
    }

    preorderSelectionState() {
      const state = { hasPreorder: false, shipDate: '', shipText: '', soldout: null, notify: null };
      this.selections.forEach((set) => {
        set.forEach((vid) => {
          if (!state.soldout && this.soldoutVariants.has(vid)) {
            state.soldout = { product: this.soldoutVariants.get(vid), variantId: vid };
          }
          if (!state.notify && this.notifyVariants.has(vid)) {
            state.notify = { product: this.notifyVariants.get(vid), variantId: vid };
          }
          const info = this.preorderVariants.get(vid);
          if (!info) return;
          state.hasPreorder = true;
          // bundle ships complete: the latest expected date wins
          if (info.shipping && (state.shipText === '' || info.date > state.shipDate)) {
            state.shipDate = info.date;
            state.shipText = info.shipping;
          }
        });
      });
      return state;
    }

    updateUI() {
      if (this.countEl) {
        this.countEl.textContent = String(this.selectedCount());
      }
      const { hasPreorder, shipText, soldout, notify } = this.preorderSelectionState();
      this.currentNotify = soldout || notify;
      if (this.atc) {
        this.atc.disabled = !this.isComplete() || !!soldout;
      }
      if (this.notifyEl) {
        this.notifyEl.hidden = !this.currentNotify;
      }
      if (this.atc) {
        this.atc.dataset.preorder = hasPreorder ? 'true' : 'false';
        if (this.atcLabelEl) {
          this.atcLabelEl.textContent = hasPreorder
            ? this.atc.dataset.labelPreorder || 'Pre-order'
            : this.atc.dataset.labelDefault || this.atcLabelEl.textContent;
        }
      }
      if (this.preorderShippingEl) {
        const shipLabel = this.preorderShippingEl.dataset.label || '';
        const shipTextEl =
          this.preorderShippingEl.querySelector('[data-bundle-preorder-shipping-text]') || this.preorderShippingEl;
        shipTextEl.textContent = shipText ? `${shipLabel ? shipLabel + ' ' : ''}${shipText}` : '';
        this.preorderShippingEl.hidden = !shipText;
      }
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
    }

    /**
     * Build /cart/add.js payload.
     * Strategy: one line item per selected variant, all tagged with the
     * same `_bundle_id` and the parent product handle. Juo / cart transform
     * uses these properties to group, discount, and apply free-gift logic.
     */
    buildCartPayload() {
      const ts = Date.now();
      const bundleInstance = `bi-${this.productId}-${ts}`;
      const items = [];

      this.config.forEach((slot) => {
        const sel = this.selections.get(slot.slot_uid);
        if (!sel || sel.size === 0) return;

        sel.forEach((variantId) => {
          items.push({
            id: variantId,
            quantity: 1,
            properties: {
              Bundel: this.productTitle,
              _box: `box-${this.productId}`,
              _bundle_instance: bundleInstance,
              _part_of: `${this.productTitle} (ref. ${String(ts).slice(-6)})`,
              _parent_product_handle: this.productHandle,
              _parent_product_id: String(this.productId),
              _bundle_slot: slot.title,
              _bundle_slot_uid: slot.slot_uid,
              ...(slot.is_free ? { _bundle_free: 'true' } : {}),
            },
          });
        });
      });

      return { items };
    }

    async submit() {
      if (!this.isComplete()) return;

      this.atc.disabled = true;
      const labelEl = this.atcLabelEl || this.atc;
      const originalText = labelEl.textContent;
      labelEl.textContent = this.atc.dataset.labelAdding || 'Adding...';

      try {
        const payload = this.buildCartPayload();
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || this.atc.dataset.errorAdd || 'Could not add bundle to cart');
        }

        // Set cart attribute so Juo's Cart Transform can apply free-item pricing
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { [`_box-${this.productId}-savings`]: '0' } }),
        });

        // Notify theme cart drawer if it listens for this
        document.dispatchEvent(new CustomEvent('bundle:added', { detail: payload }));

        // Default behavior: go to cart. Adjust to open your drawer instead.
        window.location.href = '/cart';
      } catch (err) {
        console.error('[BundleBuilder]', err);
        if (this.errorEl) {
          this.errorEl.hidden = false;
          this.errorEl.textContent = err.message || this.atc.dataset.errorGeneric || 'Something went wrong. Please try again.';
        }
        this.atc.disabled = false;
        labelEl.textContent = originalText;
      }
    }
  }

  function boot() {
    document.querySelectorAll('[data-bundle-builder]').forEach((el) => {
      if (el.__bundleBuilderInit) return;
      el.__bundleBuilderInit = true;
      new BundleBuilder(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', boot);
})();